import { useState } from 'react';
import { Eye, Zap, X as XIcon } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SignalTypeBadge } from '@/components/modules/SignalTypeBadge';
import { useGrowthStore } from '@/stores/growthStore';
import { formatCurrency, formatDate } from '@/lib/format';
import type { GrowthSignal } from '@nexus/shared/types';

const statusVariant: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  new: 'info',
  reviewed: 'warning',
  actioned: 'success',
  dismissed: 'default',
};

interface SignalSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

export function SignalSheet({ id, open, onClose }: SignalSheetProps) {
  const { signals, markReviewed, markActioned, dismiss } = useGrowthStore();
  const [loading, setLoading] = useState(false);

  const signal: GrowthSignal | undefined = signals.find((s) => s.id === id);

  if (!signal) {
    return (
      <Sheet open={open} onClose={onClose} title="Signal">
        <div className="flex items-center justify-center py-12 text-surface-500">Loading...</div>
      </Sheet>
    );
  }

  const handleAction = async (action: 'review' | 'action' | 'dismiss') => {
    setLoading(true);
    try {
      if (action === 'review') await markReviewed(signal.id);
      else if (action === 'action') await markActioned(signal.id);
      else await dismiss(signal.id);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Signal Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <p className="text-lg font-semibold text-surface-100">{signal.title}</p>
          <div className="flex items-center gap-3">
            <SignalTypeBadge type={signal.signalType} />
            <Badge variant={statusVariant[signal.status] || 'default'}>{signal.status}</Badge>
          </div>
        </div>

        {/* Score */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Score</h3>
          <div className="bg-surface-800 rounded-lg p-4 border border-surface-700">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-surface-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-nexus-500 rounded-full transition-all"
                  style={{ width: `${signal.score}%` }}
                />
              </div>
              <span className="text-lg font-semibold text-surface-200">{signal.score}</span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Details</h3>
          <div className="grid grid-cols-[120px_1fr] gap-y-3 items-start">
            <span className="text-xs text-surface-500">Description</span>
            <p className="text-sm text-surface-300">{signal.description}</p>

            <span className="text-xs text-surface-500">Projected Impact</span>
            <span className={`text-sm font-medium ${(signal.projectedImpact ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {signal.projectedImpact != null
                ? `${signal.projectedImpact >= 0 ? '+' : ''}${formatCurrency(signal.projectedImpact)}`
                : '--'}
            </span>

            <span className="text-xs text-surface-500">Created</span>
            <span className="text-sm text-surface-300">{formatDate(signal.createdAt)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-surface-700">
          {signal.status === 'new' && (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<Eye className="h-3.5 w-3.5" />}
                onClick={() => handleAction('review')}
                loading={loading}
              >
                Review
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<XIcon className="h-3.5 w-3.5" />}
                onClick={() => handleAction('dismiss')}
                loading={loading}
              >
                Dismiss
              </Button>
            </>
          )}
          {signal.status === 'reviewed' && (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<Zap className="h-3.5 w-3.5" />}
                onClick={() => handleAction('action')}
                loading={loading}
              >
                Action
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<XIcon className="h-3.5 w-3.5" />}
                onClick={() => handleAction('dismiss')}
                loading={loading}
              >
                Dismiss
              </Button>
            </>
          )}
          {(signal.status === 'actioned' || signal.status === 'dismissed') && (
            <p className="text-sm text-surface-500 italic">
              This signal has been {signal.status}.
            </p>
          )}
        </div>
      </div>
    </Sheet>
  );
}
